import { useCallback, useEffect } from 'react'
import {
  GridPaginationModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'

import { useAppDispatch } from '@user/store/hooks'
import type { FieldState } from '@user/components/xGrid'

import {
  setActivitySummaryOrderByActionCreator as setOrderBy,
  setActivitySummaryTableStateActionCreator as setTableState,
} from '../../../state'
import { ActivitySummaryHelpers } from '../../../helpers'
import { useActivitySummaryContext } from '../../../context'

type Props = {
  apiRef: ReturnType<typeof useGridApiRef>
}

export function useHandlers(props: Props) {
  const { apiRef } = props
  const appDispatch = useAppDispatch()
  const [, dispatch] = useActivitySummaryContext()

  const handlePaginationModelChange = useCallback(
    (pageModel: GridPaginationModel) => {
      const { page, pageSize } = pageModel
      dispatch(setTableState({ paginationState: { page, pageSize } }))
    },
    [dispatch],
  )

  const onChangeSortModel = useCallback(
    (sortModel?: GridSortModel) => {
      if (sortModel && !sortModel.length) return

      dispatch(setOrderBy(sortModel))
    },
    [dispatch],
  )

  useEffect(() => {
    if (!apiRef.current || !apiRef.current.subscribeEvent) {
      return
    }

    const unsubscribe = apiRef.current.subscribeEvent(
      'columnHeaderDragEnd',
      () => {
        if (!apiRef.current || !apiRef.current.getAllColumns) {
          return
        }

        const columns = apiRef.current.getAllColumns()

        const columnOrder = columns.map<FieldState>(({ field, width }) => ({
          field,
          hide: false,
          width: width?.toString(),
        }))

        ActivitySummaryHelpers.saveColOrder(appDispatch, columnOrder)
      },
    )

    return () => unsubscribe()
  }, [apiRef, appDispatch])

  useEffect(() => {
    if (!apiRef.current || !apiRef.current.subscribeEvent) {
      return
    }

    const unsubscribe = apiRef.current.subscribeEvent(
      'columnWidthChange',
      () => {
        if (!apiRef.current || !apiRef.current.getAllColumns) {
          return
        }

        const columns = apiRef.current.getAllColumns()
        const columnOrder = columns.map<FieldState>(({ field, width }) => ({
          field,
          hide: false,
          width: width?.toString(),
        }))
        ActivitySummaryHelpers.saveColOrder(appDispatch, columnOrder)
      },
    )

    return () => unsubscribe()
  }, [apiRef, appDispatch])

  return {
    handlePaginationModelChange,
    onChangeSortModel,
  }
}
